// 说明文档：https://bailongma.yuque.com/hhwu0d/nvrent/kgftedks6sie1m96
import BigNumber from 'bignumber.js'

/**
 * 函数plus用于两个数字的加法运算。
 * 使用BigNumber库以支持大数运算，避免JavaScript中浮点数运算的精度问题。
 *
 * @param {number|string} num1 - 第一个加数，可以是数字或字符串表示的数字。
 * @param {number|string} num2 - 第二个加数，可以是数字或字符串表示的数字。
 * @returns {BigNumber} 返回两个加数相加后的结果，结果为BigNumber类型。
 */
const plus = (num1, num2) => {
  return new BigNumber(num1).plus(new BigNumber(num2))
}

/**
 * 函数minus用于两个数字的减法运算。
 *
 * @param {number|string} num1 - 第一个操作数，可以是数字或数字字符串。
 * @param {number|string} num2 - 第二个操作数，可以是数字或数字字符串。
 * @returns {BigNumber} 返回num1减去num2的结果，结果是一个BigNumber对象。
 */
const minus = (num1, num2) => {
  return new BigNumber(num1).minus(new BigNumber(num2))
}

/**
 * 使用BigNumber库来执行两个数字的乘法操作。
 * 该函数接收两个数字作为参数，并返回它们的乘积。
 *
 * @param {number} num1 - 第一个乘数。
 * @param {number} num2 - 第二个乘数。
 * @returns {BigNumber} 两个数字的乘积，使用BigNumber类型以支持大数运算。
 */
const multiply = (num1, num2) => {
  return new BigNumber(num1).times(new BigNumber(num2))
}

/**
 * 使用BigNumber库执行两个数字的除法运算。
 *
 * @param {number|string} num1 - 被除数，可以是数字或字符串表示的数值。
 * @param {number|string} num2 - 除数，可以是数字或字符串表示的数值。
 * @returns {BigNumber} 返回除法运算结果的BigNumber实例。
 */
const divide = (num1, num2) => {
  return new BigNumber(num1).div(new BigNumber(num2))
}

/**
 * 函数divide2int用于对两个数字进行除法运算，并返回整数位。
 * 使用了BigNumber库以支持大数运算，确保运算的精确性。
 *
 * @param {number} num1 - 除数。该参数将作为BigNumber对象的初始化值。
 * @param {number} num2 - 被除数。同样作为BigNumber对象的初始化值。
 * @returns {BigNumber} - 返回除法运算结果四舍五入后的BigNumber对象。
 */
const divide2int = (num1, num2) => {
  return new BigNumber(num1).idiv(new BigNumber(num2))
}

/**
 * 计算两个数字的模数
 *
 * @param {number|string} num1 - 被除数，可以是数字或字符串表示的数字
 * @param {number|string} num2 - 除数，可以是数字或字符串表示的数字
 * @returns {BigNumber} 返回num1除以num2的模数结果，结果是一个BigNumber对象
 */
const mod = (num1, num2) => {
  return new BigNumber(num1).mod(new BigNumber(num2))
}

/**
 * 计算一个数的另一个数次幂，使用 BigNumber 类以支持大数运算。
 *
 * @param {number|string} num1 底数，可以是数字或字符串表示的数。
 * @param {number|string} num2 指数，可以是数字或字符串表示的数。
 * @returns {BigNumber} 返回 num1 的 num2 次幂，结果为 BigNumber 类型。
 */
const pow = (num1, num2) => {
  return new BigNumber(num1).pow(new BigNumber(num2))
}

/**
 * 计算给定数字的平方根。
 *
 * @param {number|string} num1 - 需要计算平方根的数字，可以是整数、小数或字符串表示的数字。
 * @returns {BigNumber} 返回一个 BigNumber 对象，表示输入数字的平方根。
 */
const sqrt = (num1) => {
  return new BigNumber(num1).sqrt()
}

/**
 * 比较两个数字是否相等。
 * 使用BigNumber库来确保对大数字的精确比较。
 *
 * @param {number|string} num1 - 第一个数字，可以是数字或数字字符串。
 * @param {number|string} num2 - 第二个数字，可以是数字或数字字符串。
 * @returns {boolean} 返回一个布尔值，表示num1和num2是否相等。
 */
const eq = (num1, num2) => {
  return new BigNumber(num1).eq(new BigNumber(num2))
}

/**
 * 比较两个数字的大小，返回是否num1大于num2的结果。
 * 使用BigNumber库来确保对大数字的精确比较。
 *
 * @param {number|string} num1 - 第一个数字，可以是数字或数字字符串。
 * @param {number|string} num2 - 第二个数字，可以是数字或数字字符串。
 * @returns {boolean} 如果num1大于num2，则返回true；否则返回false。
 */
const gt = (num1, num2) => {
  return new BigNumber(num1).gt(new BigNumber(num2))
}

/**
 * 检查第一个数字是否大于或等于第二个数字。
 * 使用BigNumber库来确保对大数字的正确比较。
 *
 * @param {string|number} num1 - 第一个数字，可以是字符串或数字。
 * @param {string|number} num2 - 第二个数字，可以是字符串或数字。
 * @returns {boolean} 如果num1大于或等于num2，则返回true；否则返回false。
 */
const gte = (num1, num2) => {
  return new BigNumber(num1).gte(new BigNumber(num2))
}

/**
 * 比较两个数字的大小，返回一个布尔值表示第一个数字是否小于第二个数字。
 * 使用了BigNumber库来处理可能的非常大或非常小的数字，以确保比较的准确性。
 *
 * @param {number|string} num1 - 第一个数字，可以是数字类型或字符串类型。
 * @param {number|string} num2 - 第二个数字，可以是数字类型或字符串类型。
 * @returns {boolean} 如果num1小于num2，则返回true；否则返回false。
 */
const lt = (num1, num2) => {
  return new BigNumber(num1).lt(new BigNumber(num2))
}

/**
 * 比较两个数字的大小，返回一个布尔值表示第一个数字是否小于等于第二个数字。
 * 使用了BigNumber库来处理可能的非常大或非常小的数字，以确保比较的准确性。
 *
 * @param {number|string} num1 - 第一个数字，可以是数字类型或字符串类型。
 * @param {number|string} num2 - 第二个数字，可以是数字类型或字符串类型。
 * @returns {boolean} 如果num1小于等于num2，则返回true；否则返回false。
 */
const lte = (num1, num2) => {
  return new BigNumber(num1).lte(new BigNumber(num2))
}

const ROUND_METHODS = {
  ROUND_UP: BigNumber.ROUND_UP, // 远离0取值，也就是正数向上取整 负数向下取整
  ROUND_DOWN: BigNumber.ROUND_DOWN, // 趋向0取值，也就是正数向下取整 负数向上取整
  ROUND_CEIL: BigNumber.ROUND_CEIL, // 向上取值
  ROUND_FLOOR: BigNumber.ROUND_FLOOR, // 向下取值
  ROUND_HALF_UP: BigNumber.ROUND_HALF_UP, // 四舍五入取值，如果等距就远离0
  ROUND_HALF_DOWN: BigNumber.ROUND_HALF_DOWN, // 四舍五入取值，如果等距就趋向0
  ROUND_HALF_EVEN: BigNumber.ROUND_HALF_EVEN, // 四舍五入取值，如果等距就取偶数
  ROUND_HALF_CEIL: BigNumber.ROUND_HALF_CEIL, // 四舍五入取值，向下取值
  ROUND_HALF_FLOOR: BigNumber.ROUND_HALF_FLOOR, // 四舍五入取值，向上取值
}

/**
 * 对数字进行四舍五入到指定小数位。
 * @param {number|string} num 需要进行四舍五入的数字，可以是数字或数字字符串。
 * @param {number} decimal 需要保留的小数位数。
 * @param {string} [method='ROUND_HALF_UP'] 四舍五入的方法，使用 BigNumber 的 ROUND_METHODS 对象中的方法。
 * @returns {string} 四舍五入后的数字字符串。
 */
const fixed = (num, decimal, method = 'ROUND_HALF_UP') => {
  const roundMethod = ROUND_METHODS[method]
  if (!roundMethod) throw new Error('Unsupported round method')
  return new BigNumber(num).toFixed(decimal, roundMethod)
}

/**
 * 格式化数字。
 * 使用BigNumber库来实现对数字的格式化，支持自定义前缀、小数点、千位分隔符、后缀等格式化选项。默认按整数千分位格式化
 *
 * @param {number|string} num - 要格式化的数字，可以是数字或数字字符串。
 * @param {Object} options - 格式化选项的对象，默认值如下：
 *   {
 *     prefix: '', // 数字前的前缀，默认为空字符串。
 *     decimalSeparator: '.', // 小数点符号，默认为英文句点。
 *     groupSeparator: ',', // 千位分隔符，默认为英文逗号。
 *     groupSize: 3, // 主组大小，默认为3。
 *     secondaryGroupSize: 0, // 副组大小，默认为0。
 *     fractionGroupSeparator: '', // 分数组分隔符，默认为空字符串。
 *     fractionGroupSize: 0, // 分数组大小，默认为0。
 *     suffix: '', // 数字后的后缀，默认为空字符串。
 *   }
 * @returns {string} 格式化后的数字字符串。
 */
const format = (num, options) => {
  try {
    const result = new BigNumber(num).toFormat(2, options)
    return result === 'NaN' ? '--' : result
  } catch (error) {
    return '--'
  }
}

export const numberUtils = {
  BigNumber,
  plus,
  minus,
  multiply,
  divide,
  divide2int,
  mod,
  pow,
  sqrt,
  eq,
  gt,
  gte,
  lt,
  lte,
  fixed,
  format,
}
